import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Editor } from "@tinymce/tinymce-react";
import { toast } from "react-toastify";
import {
  getBlogById,
  createBlog,
  updateBlog,
  authAPI,
  getCategories,
  getAuthors,
  API_BASE_URL,
} from "../../../services/api";

export default function CreateOrEditBlog() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [content, setContent] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [existingFeaturedImage, setExistingFeaturedImage] = useState("");
  const [removeFeaturedImageFlag, setRemoveFeaturedImageFlag] = useState(false);
  const [categoryId, setCategoryId] = useState("");
  const [authorId, setAuthorId] = useState("");
  const [status, setStatus] = useState("draft");
  const [categories, setCategories] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [manualSlugEdit, setManualSlugEdit] = useState(false);
  const [metaTitle, setMetaTitle] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [metaKeywords, setMetaKeywords] = useState("");
  const [canonicalUrl, setCanonicalUrl] = useState("");
  const [noIndex, setNoIndex] = useState(false);

  const generateSlug = (text) => {
    return text
      .toLowerCase()
      .trim()
      .replace(/'/g, '') 
      .replace(/[^a-z0-9\s-]/g, '') 
      .replace(/\s+/g, '-') 
      .replace(/-+/g, '-') 
      .replace(/^-+|-+$/g, ''); 
  };

  
  useEffect(() => {
    if (!manualSlugEdit && title && !id) {
      setSlug(generateSlug(title));
    }
  }, [title, manualSlugEdit, id]);

  useEffect(() => {
    if (!authAPI.isAuthenticated()) {
      toast.error("You must be logged in", { theme: "colored" });
      navigate("/login");
      return;
    }
    fetchDropdownData();
  }, [navigate]);

  useEffect(() => {
    const handleFocus = () => {
      fetchDropdownData();
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, []);

  useEffect(() => {
    if (id && authAPI.isAuthenticated()) {
      fetchBlog();
    }
  }, [id]);

  const fetchDropdownData = async () => {
    try {
      const [categoriesData, authorsData] = await Promise.all([
        getCategories(),
        getAuthors(),
      ]);
      setCategories(categoriesData);
      setAuthors(authorsData);
    } catch (err) {
      console.error("Fetch dropdown data error:", err);
    }
  };

  const fetchBlog = async () => {
    try {
      setLoading(true);
      const blog = await getBlogById(id);
      setTitle(blog.title);
      setSlug(blog.slug || "");
      setContent(blog.content);
      setExcerpt(blog.excerpt || "");
      setCategoryId(blog.CategoryId || "");
      setAuthorId(blog.AuthorId || "");
      setStatus(blog.status || "draft");
      setMetaTitle(blog.metaTitle || "");
    setMetaDescription(blog.metaDescription || "");
    setMetaKeywords(blog.metaKeywords || "");
    setCanonicalUrl(blog.canonicalUrl || "");
    setNoIndex(blog.noIndex || false);
      if (blog.featuredImage) {
        const normalized = blog.featuredImage.startsWith("/")
          ? blog.featuredImage
          : `/${blog.featuredImage}`;
        setExistingFeaturedImage(normalized);
        setImagePreview(`${API_BASE_URL.replace("/api", "")}${normalized}`);
      } else {
        setExistingFeaturedImage("");
        setImagePreview("");
      }
      setRemoveFeaturedImageFlag(false);
    } catch (err) {
      console.error("Fetch blog error:", err);
      setError(err.message || "Failed to load blog");
      toast.error(err.message || "Failed to load blog", { theme: "colored" });
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size must be less than 5MB", { theme: "colored" });
        return;
      }
      if (!file.type.startsWith("image/")) {
        toast.error("Please select an image file", { theme: "colored" });
        return;
      }
      setImage(file);
      setImagePreview(URL.createObjectURL(file));
      setExistingFeaturedImage("");
      setRemoveFeaturedImageFlag(false);
    } else {
      setImage(null);
      setImagePreview("");
    }
  };

  const handleRemoveFeaturedImage = () => {
    setImage(null);
    setImagePreview("");
    if (existingFeaturedImage) {
      setRemoveFeaturedImageFlag(true);
      setExistingFeaturedImage("");
    }
  };

  const handleEditorChange = (content, editor) => {
    setContent(content);
  };

  const handleSlugChange = (e) => {
    const newSlug = e.target.value;
    setSlug(generateSlug(newSlug));
    setManualSlugEdit(true);
  };

  const handleRegenerateSlug = () => {
    if (title) {
      setSlug(generateSlug(title));
      setManualSlugEdit(false);
      toast.info("Slug regenerated from title", { theme: "colored" });
    } else {
      toast.warning("Please enter a title first", { theme: "colored" });
    }
  };

  const getAuthToken = () => localStorage.getItem("adminToken");

  const isTokenExpired = () => {
    const token = getAuthToken();
    if (!token) return true;
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      if (payload.exp && payload.exp < currentTime) {
        return true;
      }
      return false;
    } catch (error) {
      return true;
    }
  };

  const extractErrorMessage = (err) => {
    if (!err) return "";
    if (typeof err === "string") return err;
    if (err.response?.data?.message) return err.response.data.message;
    if (err.message) return err.message;
    return "";
  };

  const handleImageUpload = async (blobInfo) => {
    try {
      if (isTokenExpired()) {
        authAPI.logout();
        toast.error("Your session has expired. Please login again.", { theme: "colored" });
        navigate("/login");
        throw new Error("Session expired. Please login again.");
      }

      const token = getAuthToken();
      const formData = new FormData();
      formData.append("image", blobInfo.blob(), blobInfo.filename());
      const response = await fetch(`${API_BASE_URL}/blogs/upload-image`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });
      const result = await response.json();

      if (
        response.status === 401 &&
        (result.expired || (result.message && result.message.toLowerCase().includes("expired")))
      ) {
        authAPI.logout();
        toast.error("Your session has expired. Please login again.", { theme: "colored" });
        navigate("/login");
        throw new Error("Session expired. Please login again.");
      }

      if (!response.ok || !result?.location) {
        throw new Error(result?.message || "Failed to upload image");
      }

      const location = result.location.startsWith("/")
        ? result.location
        : `/${result.location}`;
      return `${API_BASE_URL.replace("/api", "")}${location}`;
    } catch (error) {
      const errorMsg = extractErrorMessage(error);
      toast.error(errorMsg || "Failed to upload image", { theme: "colored" });
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!authAPI.isAuthenticated()) {
      toast.error("You must be logged in", { theme: "colored" });
      return navigate("/login");
    }

    if (!title.trim()) {
      setError("Title is required");
      toast.error("Title is required", { theme: "colored" });
      return;
    }

    if (!slug.trim()) {
      setError("URL slug is required");
      toast.error("URL slug is required", { theme: "colored" });
      return;
    }

    if (!content.trim() || content === "<p><br></p>" || content === "") {
      setError("Content is required");
      toast.error("Content is required", { theme: "colored" });
      return;
    }

    if (!image && !imagePreview && !existingFeaturedImage) {
      setError("Featured image is required");
      toast.error("Featured image is required", { theme: "colored" });
      return;
    }

    setLoading(true);

    try {
      const blogData = {
        title: title.trim(),
        slug: slug.trim(),
        content: content,
        excerpt: excerpt.trim() || null,
        CategoryId: categoryId || null,
        AuthorId: authorId || null,
        status: status,
        metaTitle: metaTitle.trim() || null,
        metaDescription: metaDescription.trim() || null,
        metaKeywords: metaKeywords.trim() || null,
        canonicalUrl: canonicalUrl.trim() || null,
        noIndex: noIndex,
      };

      if (image) {
        blogData.featuredImage = image;
      }
      if (id && removeFeaturedImageFlag && !image) {
        blogData.removeFeaturedImage = true;
      }

      let response;
      if (id) {
        response = await updateBlog(id, blogData);
      } else {
        response = await createBlog(blogData);
      }

      

      if (response.success) {
        toast.success(
          response.message || (id ? "Blog updated! ✅" : "Blog created! ✅"),
          { theme: "colored" }
        );
        
        try {
          localStorage.setItem("blogChanged", Date.now().toString());
          window.dispatchEvent(new Event("blogChanged"));
        } catch {}
        
        
        navigate("/blog-admin");
      } else {
        const errorMsg = response.message || "Failed to save blog";
        setError(errorMsg);
        toast.error(errorMsg, { theme: "colored" });
      }
    } catch (err) {
      console.error("Submit error:", err);
      const errorMsg = extractErrorMessage(err);
      setError(errorMsg || "Failed to save blog");
      toast.error(errorMsg || "Failed to save blog", { theme: "colored" });
    } finally {
      setLoading(false);
    }
  };

  if (loading && id) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50 dark:bg-[#141D26]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-primaryBlue mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Loading blog...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#141D26] py-8">
      <div className="max-w-5xl mx-auto px-4">
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-800 dark:text-white">
            {id ? "Edit Blog Post" : "Create New Blog Post"}
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mt-2">
            {id
              ? "Update your blog post"
              : "Fill in the details to create a new blog"}
          </p>
        </div>

        {error && (
          <div className="bg-red-100 dark:bg-red-900/20 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-400 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          className="bg-white dark:bg-slate-800 rounded-lg shadow-md p-6"
        >
          <div className="space-y-6">
            {/* Title */}
            <div>
              <label className="block text-gray-700 dark:text-gray-300 text-sm font-semibold mb-2">
                Blog Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className="w-full p-3 border border-gray-300 dark:border-gray-600 dark:bg-slate-700 dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter blog title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                disabled={loading}
              />
            </div>

            {/* URL Slug */}
            <div>
              <label className="block text-gray-700 dark:text-gray-300 text-sm font-semibold mb-2">
                URL Slug <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  className="flex-1 p-3 border border-gray-300 dark:border-gray-600 dark:bg-slate-700 dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  placeholder="e.g., nigerias-best-cloud"
                  value={slug}
                  onChange={handleSlugChange}
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={handleRegenerateSlug}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition text-sm font-medium"
                  disabled={loading}
                  title="Regenerate slug from title"
                >
                  🔄 Auto
                </button>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                URL: <span className="font-mono text-blue-600 dark:text-blue-400">
                  cloudvantage.com.ng/blog/{slug || 'your-slug-here'}
                </span>
              </p>
            </div>

            {/* Category, Author & Status */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-gray-700 dark:text-gray-300 text-sm font-semibold mb-2">
                  Category
                </label>
                <select
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 dark:bg-slate-700 dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                  disabled={loading}
                >
                  <option value="">Select Category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-gray-700 dark:text-gray-300 text-sm font-semibold mb-2">
                  Author
                </label>
                <select
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 dark:bg-slate-700 dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={authorId}
                  onChange={(e) => setAuthorId(e.target.value)}
                  disabled={loading}
                >
                  <option value="">Select Author</option>
                  {authors.map((author) => (
                    <option key={author.id} value={author.id}>
                      {author.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-gray-700 dark:text-gray-300 text-sm font-semibold mb-2">
                  Status
                </label>
                <select
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 dark:bg-slate-700 dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  disabled={loading}
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
              </div>
            </div>

            {/* Featured Image */}
            <div>
              <label className="block text-gray-700 dark:text-gray-300 text-sm font-semibold mb-2">
                Featured Image <span className="text-red-500">*</span>
              </label>
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                className="w-full p-3 border border-gray-300 dark:border-gray-600 dark:bg-slate-700 dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                onChange={handleImageChange}
                disabled={loading}
                required={!id && !imagePreview && !existingFeaturedImage}
              />
              {(imagePreview || existingFeaturedImage) && (
                <div className="mt-3 relative">
                  <img
                    src={
                      imagePreview ||
                      `${API_BASE_URL.replace("/api", "")}${existingFeaturedImage}`
                    }
                    alt="Featured Preview"
                    className="w-full h-64 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={handleRemoveFeaturedImage}
                    className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600"
                  >
                    ✕
                  </button>
                </div>
              )}
            </div>

            {/* Excerpt */}
            <div>
              <label className="block text-gray-700 dark:text-gray-300 text-sm font-semibold mb-2">
                Excerpt (Optional)
              </label>
              <textarea
                className="w-full p-3 border border-gray-300 dark:border-gray-600 dark:bg-slate-700 dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Brief description of the blog post"
                value={excerpt}
                onChange={(e) => setExcerpt(e.target.value)}
                rows="3"
                disabled={loading}
              />
            </div>

            {/* Content Editor */}
            <div>
              <label className="block text-gray-700 dark:text-gray-300 text-sm font-semibold mb-2">
                Blog Content <span className="text-red-500">*</span>
              </label>
              <Editor
                apiKey={process.env.REACT_APP_BLOG_API_KEY}
                value={content}
                onEditorChange={handleEditorChange}
                init={{
                  height: 500,
                  menubar: true,
                  automatic_uploads: true,
                  images_upload_handler: async (blobInfo, progress) =>
                    handleImageUpload(blobInfo),
                  plugins: [
                    "advlist",
                    "autolink",
                    "lists",
                    "link",
                    "image",
                    "charmap",
                    "preview",
                    "anchor",
                    "searchreplace",
                    "visualblocks",
                    "code",
                    "fullscreen",
                    "insertdatetime",
                    "media",
                    "table",
                    "code",
                    "help",
                    "wordcount",
                  ],
                  toolbar:
                    "undo redo | blocks | " +
                    "bold italic forecolor | alignleft aligncenter " +
                    "alignright alignjustify | bullist numlist outdent indent | " +
                    "image media | removeformat | help",
                  content_style:
                    "body { font-family:Helvetica,Arial,sans-serif; font-size:14px } " +
                    "h1 { font-size: 2em; font-weight: bold; margin: 0.67em 0; } " +
                    "h2 { font-size: 1.5em; font-weight: bold; margin: 0.75em 0; } " +
                    "h3 { font-size: 1.17em; font-weight: bold; margin: 0.83em 0; } " +
                    "h4 { font-size: 1em; font-weight: bold; margin: 1.12em 0; } " +
                    "h5 { font-size: 0.83em; font-weight: bold; margin: 1.5em 0; } " +
                    "h6 { font-size: 0.75em; font-weight: bold; margin: 1.67em 0; }",
                }}
                disabled={loading}
              />
            </div>

            {/* SEO Settings Section - Add after Content Editor */}
            <div className="border-t pt-6 mt-6">
              <h2 className="text-xl font-bold mb-4 text-gray-800 dark:text-white">
                SEO Settings
              </h2>
              
              {/* Meta Title */}
              <div className="mb-4">
                <label className="block text-gray-700 dark:text-gray-300 text-sm font-semibold mb-2">
                  Meta Title (SEO)
                </label>
                <input
                  type="text"
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 dark:bg-slate-700 dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Leave empty to use blog title"
                  value={metaTitle}
                  onChange={(e) => setMetaTitle(e.target.value)}
                  maxLength="60"
                  disabled={loading}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {metaTitle.length}/60 characters (optimal: 50-60)
                </p>
              </div>
              
              {/* Meta Description */}
              <div className="mb-4">
                <label className="block text-gray-700 dark:text-gray-300 text-sm font-semibold mb-2">
                  Meta Description (SEO)
                </label>
                <textarea
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 dark:bg-slate-700 dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Brief description for search engines"
                  value={metaDescription}
                  onChange={(e) => setMetaDescription(e.target.value)}
                  rows="3"
                  maxLength="160"
                  disabled={loading}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {metaDescription.length}/160 characters (optimal: 120-160)
                </p>
              </div>
              
              {/* Keywords */}
              <div className="mb-4">
                <label className="block text-gray-700 dark:text-gray-300 text-sm font-semibold mb-2">
                  Meta Keywords
                </label>
                <input
                  type="text"
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 dark:bg-slate-700 dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="cloud, computing, nigeria, technology"
                  value={metaKeywords}
                  onChange={(e) => setMetaKeywords(e.target.value)}
                  disabled={loading}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Comma-separated keywords
                </p>
              </div>
              
              {/* Canonical URL */}
              <div className="mb-4">
                <label className="block text-gray-700 dark:text-gray-300 text-sm font-semibold mb-2">
                  Canonical URL (Optional)
                </label>
                <input
                  type="url"
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 dark:bg-slate-700 dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  placeholder="https://cloudvantage.com.ng/blog/your-post"
                  value={canonicalUrl}
                  onChange={(e) => setCanonicalUrl(e.target.value)}
                  disabled={loading}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Use for duplicate content prevention
                </p>
              </div>
              
              {/* No Index */}
              <div className="flex items-center mb-4">
                <input
                  type="checkbox"
                  id="noIndex"
                  checked={noIndex}
                  onChange={(e) => setNoIndex(e.target.checked)}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                  disabled={loading}
                />
                <label htmlFor="noIndex" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Prevent search engines from indexing this page (noindex)
                </label>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-4 pt-4">
              <button
                type="submit"
                className="flex-1 bg-blue-600 dark:bg-primaryBlue text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 dark:hover:bg-primaryBlue/90 transition disabled:bg-gray-400"
                disabled={loading}
              >
                {loading ? "Saving..." : id ? "Update Blog" : "Create Blog"}
              </button>
              <button
                type="button"
                onClick={() => navigate("/blog-admin")}
                className="bg-gray-600 dark:bg-gray-700 text-white py-3 px-6 rounded-lg font-semibold hover:bg-gray-700 dark:hover:bg-gray-600 transition"
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

